import {HttpClient, HttpResponse} from "@angular/common/http";
import {inject, Injectable} from "@angular/core";
import {Observable, Subject, tap} from "rxjs";
import {OpportunityDetails, OpportunityType, OrderBookingRequest, OrderBookingResponse} from "../types/booking.types";
import {EntityResponse} from "../types/types";
import {ApiSettingsUtils} from "../configuration/configuration.types";
import {environment} from "../../../environments/environment";
import {ChangeOrderModel, ChangeOrderResponse, SignatureRequest} from "../types/change-order.types";
import {DispatchOrderResponse, OrderDispatchDetails} from "../types/dispatch.types";
import {BolFileUploadResponse, BolUploadOrderDetails} from "../types/bol-upload.types";
import {FileUri} from "./file.service";

@Injectable({
    providedIn: "root",
})
export class OpportunityService {
    private _httpClient = inject(HttpClient);
    private _baseUrl!: string;
    private _opportunity = new Subject<OpportunityDetails>();

    constructor() {
        this._baseUrl = ApiSettingsUtils.getBaseApiUrl(environment.apiSettings);
    }

    get opportunity$(): Observable<OpportunityDetails> {
        return this._opportunity.asObservable();
    }

    setOpportunity(opportunity: OpportunityDetails): void {
        this._opportunity.next(opportunity);
    }

    getDetails(opportunityType: OpportunityType): Observable<EntityResponse<OpportunityDetails>> {
        return this._httpClient
            .get<EntityResponse<OpportunityDetails>>(`${this._baseUrl}/${opportunityType}`)
            .pipe(
                tap((opportunity: EntityResponse<OpportunityDetails>) => this.setOpportunity(opportunity.data))
            );
    }

    bookOrder(payload: OrderBookingRequest): Observable<EntityResponse<OrderBookingResponse>> {
        return this._httpClient.post<EntityResponse<OrderBookingResponse>>(`${this._baseUrl}/orders`, payload);
    }

    getChangeOrderDetails(): Observable<EntityResponse<ChangeOrderModel>> {
        return this._httpClient.get<EntityResponse<ChangeOrderModel>>(`${this._baseUrl}/orders/change-order`);
    }

    signChangeOrder(payload: SignatureRequest): Observable<EntityResponse<ChangeOrderResponse>> {
        return this._httpClient.put<EntityResponse<ChangeOrderResponse>>(`${this._baseUrl}/orders/sign-change-order`, payload);
    }

    getDispatchOrderDetails(): Observable<EntityResponse<OrderDispatchDetails>> {
        return this._httpClient.get<EntityResponse<OrderDispatchDetails>>(`${this._baseUrl}/orders/dispatch`);
    }

    signDispatchOrder(payload: SignatureRequest): Observable<EntityResponse<DispatchOrderResponse>> {
        return this._httpClient.put<EntityResponse<DispatchOrderResponse>>(`${this._baseUrl}/orders/sign-dispatch`, payload);
    }

    getDispatchSheet(): Observable<HttpResponse<Blob>> {
        return this._httpClient.get(`${this._baseUrl}/orders/dispatch-sheet`, {observe: 'response', responseType: 'blob'});
    }

    getBolUploadOrderDetails(): Observable<EntityResponse<BolUploadOrderDetails>> {
        return this._httpClient.get<EntityResponse<BolUploadOrderDetails>>(`${this._baseUrl}/orders/bol-upload-info`);
    }

    uploadBolFile(file: File): Observable<EntityResponse<BolFileUploadResponse>> {
        const formData = new FormData();
        formData.append('file', file);
        return this._httpClient.post<EntityResponse<BolFileUploadResponse>>(`${this._baseUrl}/orders/bol-files`, formData);
    }

    deleteBolFile(fileId: string): Observable<void> {
        return this._httpClient.delete<void>(`${this._baseUrl}/orders/bol-files/${fileId}`);
    }

    getSummary(): Observable<FileUri> {
        return this._httpClient.get<FileUri>(`${this._baseUrl}/orders/summary`);
    }
}
